import * as React from "react";

export interface UseSvgToPngImageOptions {
  /** The live SVG element to convert */
  svg: SVGSVGElement | null;
  /** Scale factor for rasterization (default 3) */
  scale?: number;
  /** Gate the conversion (default true) */
  enabled?: boolean;
  /** Enable verbose debugging in console (default false) */
  debug?: boolean;
}

export interface UseSvgToPngImageResult {
  /** data:image/png;base64,...  or null while pending/failure */
  imgSrc: string | null;
  /** "idle" | "serializing" | "loading-image" | "drawing" | "done" | "error" */
  status:
    | "idle"
    | "serializing"
    | "loading-image"
    | "drawing"
    | "done"
    | "error";
  /** Error message if any */
  error: string | null;
  /** Force re-run (e.g., after external changes) */
  regenerate: () => void;
}

/**
 * Converts an SVGElement to a PNG data URL using a Canvas at `scale` (default 3x).
 * Handles size detection (viewBox/width/height/bbox), sets explicit width/height for crisp rasterization,
 * and includes debug logging helpers.
 */
export function useSvgToPngImage({
  svg,
  scale = 3,
  enabled = true,
  debug = false
}: UseSvgToPngImageOptions): UseSvgToPngImageResult {
  const [imgSrc, setImgSrc] = React.useState<string | null>(null);
  const [status, setStatus] =
    React.useState<UseSvgToPngImageResult["status"]>("idle");
  const [error, setError] = React.useState<string | null>(null);

  const regenKeyRef = React.useRef(0);
  const regenerate = React.useCallback(() => {
    regenKeyRef.current++;
    // reset state to allow UI to react
    setImgSrc(null);
    setStatus("idle");
    setError(null);
  }, []);

  const lastSerializedRef = React.useRef<string | null>(null);

  const log = React.useCallback(
    (...args: any[]) => {
      if (debug) console.log("[useSvgToPngImage]", ...args);
    },
    [debug]
  );

  const getSvgSize = React.useCallback((node: SVGSVGElement) => {
    // Try viewBox first
    const vb = node.getAttribute("viewBox");
    if (vb) {
      const parts = vb.trim().split(/\s+/).map(Number);
      if (parts.length === 4 && parts.every(Number.isFinite)) {
        const [, , w, h] = parts;
        if (w > 0 && h > 0) {
          return { width: w, height: h };
        }
      }
    }
    // Then explicit width/height attr
    const parseLen = (v: string | null) =>
      v ? Number(String(v).replace(/[^0-9.]+/g, "")) : NaN;
    const wAttr = parseLen(node.getAttribute("width"));
    const hAttr = parseLen(node.getAttribute("height"));
    if (wAttr > 0 && hAttr > 0) return { width: wAttr, height: hAttr };

    // getBBox can throw if not in DOM / no layout yet
    try {
      const bbox = node.getBBox?.();
      if (bbox?.width > 0 && bbox?.height > 0) {
        return { width: bbox.width, height: bbox.height };
      }
    } catch {}

    // Finally, layout box (may be 0 if hidden)
    const rect = node.getBoundingClientRect();
    return {
      width: Math.max(1, rect.width || 1),
      height: Math.max(1, rect.height || 1)
    };
  }, []);

  const serializeSvg = React.useCallback(
    (node: SVGSVGElement, targetW: number, targetH: number) => {
      const clone = node.cloneNode(true) as SVGSVGElement;

      // Ensure xmlns
      if (!clone.getAttribute("xmlns")) {
        clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
      }
      // Fix dimensions to rasterize crisply at scale
      clone.setAttribute("width", String(targetW));
      clone.setAttribute("height", String(targetH));

      // Remove inline event handlers for safety
      clone.querySelectorAll("*").forEach((el) => {
        for (const attr of Array.from(el.attributes)) {
          if (/^on/i.test(attr.name)) el.removeAttribute(attr.name);
        }
      });

      const xml = new XMLSerializer().serializeToString(clone);
      const finalXml = `<?xml version="1.0" standalone="no"?>\n${xml}`;
      return finalXml;
    },
    []
  );

  React.useEffect(() => {
    if (!enabled || !!imgSrc) return;
    let cancelled = false;

    async function run() {
      if (!enabled || !svg || !!imgSrc) {
        log("skip: enabled=", enabled, "svg=", !!svg);
        return;
      }

      setStatus("serializing");
      setError(null);

      // Measure
      const { width, height } = getSvgSize(svg);
      const targetW = Math.max(1, Math.round(width * scale));
      const targetH = Math.max(1, Math.round(height * scale));

      log("size", { width, height, targetW, targetH });

      if (targetW <= 0 || targetH <= 0) {
        setStatus("error");
        setError("SVG has no measurable size.");
        return;
      }

      // Serialize
      const xml = serializeSvg(svg, targetW, targetH);
      if (debug) {
        // careful: big strings
        console.log("[useSvgToPngImage] serialized SVG length:", xml.length);
      }

      // Prevent unnecessary rework
      if (lastSerializedRef.current === xml && imgSrc) {
        log("skip: same serialization, already have imgSrc");
        setStatus("done");
        return;
      }
      lastSerializedRef.current = xml;

      // Blob & URL
      const blob = new Blob([xml], { type: "image/svg+xml;charset=utf-8" });
      const url = URL.createObjectURL(blob);

      try {
        setStatus("loading-image");
        const image = await new Promise<HTMLImageElement>((resolve, reject) => {
          const img = new Image();
          // Uncomment if your SVG references external images and CORS allows it
          // img.crossOrigin = "anonymous";
          img.onload = () => resolve(img);
          img.onerror = (e) => {
            log("Image load failed", e);
            reject(new Error("Failed to load serialized SVG"));
          };
          img.src = url;
        });

        if (cancelled) return;

        setStatus("drawing");

        const canvas = document.createElement("canvas");
        canvas.width = targetW;
        canvas.height = targetH;

        const ctx = canvas.getContext("2d");
        if (!ctx) {
          throw new Error("Canvas 2D context not available");
        }

        ctx.clearRect(0, 0, targetW, targetH);
        ctx.drawImage(image, 0, 0, targetW, targetH);

        const dataUrl = canvas.toDataURL("image/png");
        log("png len", dataUrl.length);

        if (!dataUrl || dataUrl === "data:,") {
          throw new Error("Canvas produced an empty data URL (data:,).");
        }

        if (!cancelled) {
          setImgSrc(dataUrl);
          setStatus("done");
          setError(null);
        }
      } catch (e: any) {
        if (!cancelled) {
          setStatus("error");
          setError(e?.message || "PNG conversion failed");
          setImgSrc(null);
        }
      } finally {
        URL.revokeObjectURL(url);
      }
    }

    run();

    return () => {
      cancelled = true;
    };
    // Re-run if svg ref changes, scale/enabled/debug, or explicit regenerate()
  }, [enabled]);

  return { imgSrc, status, error, regenerate };
}
